import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'
import { VariantProps, cva } from 'class-variance-authority'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import {
  ComponentPropsWithoutRef,
  ElementRef,
  MouseEvent,
  PointerEvent,
  ReactNode,
  forwardRef,
  useState,
} from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import type { MenuSeparator } from '@/components/shared/submit-select/types'

export type { MenuSeparator }

const DropdownMenuRoot = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-max min-w-[8rem] overflow-hidden rounded-sm border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('mx-1.5 my-1.5 h-px bg-main-border-default', className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const commonTriggerStyles =
  'border border-solid whitespace-nowrap rounded py-2 px-4 flex gap-1.5 items-center border-gray-200'
const commonDisabledStyles = 'cursor-not-allowed opacity-50'
const commonTransitionStyles = 'transition duration-300 ease-in-out'

const dropdownVariants = cva('z-max flex flex-col gap-1 rounded', {
  variants: {
    variant: {
      default: 'bg-gray-700 text-white',
      secondary: 'text-gray-700',
      custom: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const dropdownTriggerVariants = cva('', {
  variants: {
    triggerAppearance: {
      headless: '',
      default: `${commonTriggerStyles} text-gray-400`,
      secondary: `${commonTriggerStyles} text-gray-700`,
    },
    triggerDisabled: {
      true: commonDisabledStyles,
      false: '',
    },
  },
  defaultVariants: {
    triggerAppearance: 'default',
    triggerDisabled: false,
  },
  compoundVariants: [
    {
      triggerAppearance: 'default',
      triggerDisabled: false,
      className: `${commonTransitionStyles} hover:bg-gray-700 hover:text-white`,
    },
    {
      triggerAppearance: 'secondary',
      triggerDisabled: false,
      className: `${commonTransitionStyles} hover:bg-gray-200`,
    },
  ],
})

export type DropdownMenuActionItem = {
  content: string | ReactNode
  icon?: ReactNode
  onClick?: (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void
  itemDisabled?: boolean
  disabledTooltip?: string
  hide?: boolean
  itemClassName?: string
  // Marks the active choice in a single-select menu.
  selected?: boolean
}

export type DropdownMenuProps = {
  triggerContent: ReactNode
  menuItems: (DropdownMenuActionItem | MenuSeparator | string)[]
  openOnRightClick?: boolean
  side?: 'top' | 'bottom' | 'left' | 'right'
  triggerClassName?: string
  contentClassName?: string
  align?: DropdownMenuContentProps['align']
} & VariantProps<typeof dropdownTriggerVariants> &
  VariantProps<typeof dropdownVariants>

export const DropdownMenu = ({
  triggerContent,
  menuItems,
  triggerAppearance,
  triggerDisabled,
  variant,
  openOnRightClick = false,
  triggerClassName,
  side = 'bottom',
  contentClassName,
  align,
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false)

  const handleContextMenu = (e: MouseEvent<HTMLButtonElement>) => {
    if (openOnRightClick) {
      e.preventDefault()
      setOpen(true)
    }
  }

  const handlePointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (openOnRightClick && e.button === 0) {
      e.preventDefault()
    }
  }

  const contentVariantClasses =
    variant === 'custom'
      ? ''
      : cn('z-max flex flex-col gap-1 rounded', dropdownVariants({ variant }))

  // Must be defined separately
  // Otherwise it complains about asChild + 2 children
  const triggerChildren =
    triggerAppearance === 'headless' ? (
      triggerContent
    ) : (
      <>
        {triggerContent}
        <ChevronDownIcon className='w-4' />
      </>
    )

  return (
    <DropdownMenuRoot open={open} onOpenChange={setOpen} modal={false}>
      {/* Headless trigger MUST have asChild and 1 child element, so we render it differently */}
      <DropdownMenuTrigger
        asChild={triggerAppearance === 'headless'}
        disabled={!!triggerDisabled}
        className={cn(
          dropdownTriggerVariants({ triggerAppearance, triggerDisabled }),
          triggerClassName,
        )}
        onContextMenu={handleContextMenu}
        onPointerDown={handlePointerDown}
        onClick={(e) => e.stopPropagation()}
      >
        {triggerChildren}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align ?? (triggerAppearance === 'headless' ? 'end' : 'center')}
        side={side}
        className={cn(contentVariantClasses, contentClassName)}
      >
        {menuItems.map((item, index) => {
          if (typeof item === 'string') {
            return (
              <div key={index} className='px-2 py-1.5 text-white text-label-overline'>
                {item}
              </div>
            )
          }

          if ('type' in item) {
            return <DropdownMenuSeparator key={index} />
          }

          const {
            content,
            onClick,
            icon,
            itemDisabled,
            disabledTooltip,
            hide = false,
            itemClassName,
            selected,
          } = item

          if (hide) return null

          return (
            <Tooltip
              key={index}
              tooltipDisabled={!itemDisabled || !disabledTooltip}
              trigger={
                <DropdownMenuItem
                  onClick={!itemDisabled ? (e) => onClick?.(e) : undefined}
                  className={cn(
                    'cursor-pointer',
                    selected &&
                      'bg-main-background-interactive-list-selected hover:!bg-main-background-interactive-list-selectedHover',
                    itemClassName,
                  )}
                  disabled={itemDisabled}
                >
                  <TextWithIcon text={content} icon={icon} className='gap-1.5' />
                  {selected && (
                    <CheckIcon className='ml-auto size-4 shrink-0 text-main-foreground-accent' />
                  )}
                </DropdownMenuItem>
              }
            >
              {disabledTooltip}
            </Tooltip>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}
